import { useState, useRef } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { EditModeToggle } from './EditModeToggle';
import { useSession } from '@/contexts/SessionContext';
import { ActiveViewers, type ActiveViewer } from './ActiveViewers';

interface HeaderProps {
  sessionId?: string;
  currentUser?: string;
  isReconnecting?: boolean;
  onSwitchSession?: () => void;
  /** Other people with this session open and in use right now. */
  viewers?: ActiveViewer[];
}

export function Header({
  sessionId,
  currentUser,
  isReconnecting,
  onSwitchSession,
  viewers = [],
}: HeaderProps) {
  const { accessMode } = useSession();
  // Opened from the past-sessions list: the ID was never given, so never shown.
  const hideSessionId = accessMode === 'browse';
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopySessionId = () => {
    if (!sessionId || hideSessionId) return;
    navigator.clipboard.writeText(sessionId).then(() => {
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  };

  return (
    <>
    <header
      className="ui-zoom"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '7px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ maxWidth: 'none', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: '20px', lineHeight: '24px', fontWeight: 700, letterSpacing: '-0.3px', flexShrink: 0 }}>
              ORCA
            </h1>
            {currentUser && (
              <span
                className="header-username"
                title={currentUser}
                style={{
                  height: '24px', boxSizing: 'border-box', display: 'flex', alignItems: 'center',
                  padding: '0 9px', borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.14)',
                  fontSize: '12px', fontWeight: 600, color: 'white', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
                }}
              >
                {currentUser}
              </span>
            )}
          </div>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.62)', letterSpacing: '0.6px', fontWeight: 400 }}>
            Officer Roster &amp; Counter Allocator
          </span>
        </div>

        {sessionId && (
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', justifyContent: 'flex-end', minWidth: 0 }}>
            <ActiveViewers viewers={viewers} />
            <EditModeToggle />
            <div
              onClick={handleCopySessionId}
              title={hideSessionId ? 'Opened from past sessions' : 'Click to copy session ID'}
              style={{
                background: copied ? 'rgba(134,239,172,0.35)' : 'rgba(255,255,255,0.15)',
                borderRadius: '6px',
                padding: '3px 9px',
                fontSize: '12px',
                fontFamily: 'monospace',
                letterSpacing: '1px',
                cursor: hideSessionId ? 'default' : 'pointer',
                transition: 'background 0.2s',
                userSelect: 'none',
              }}
            >
              {hideSessionId ? 'Past session' : copied ? '✓ Copied!' : sessionId}
            </div>
            {isReconnecting && (
              <span
                title="Reconnecting..."
                style={{
                  display: 'inline-block',
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.65)',
                  animation: 'ws-dot-pulse 1.4s ease-in-out infinite',
                  flexShrink: 0,
                }}
              />
            )}
            {onSwitchSession && (
              <button
                onClick={() => setShowSwitchConfirm(true)}
                title="Log out of this session"
                aria-label="Log out of this session"
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '4px 6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <LogoutIcon />
              </button>
            )}
          </div>
        )}
      </div>
    </header>

    {showSwitchConfirm && (
      <ConfirmDialog
        title="Are you sure?"
        message="All edits saved."
        confirmLabel="Log out"
        onConfirm={() => { setShowSwitchConfirm(false); onSwitchSession?.(); }}
        onCancel={() => setShowSwitchConfirm(false)}
      />
    )}
    </>
  );
}

function LogoutIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
